<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon kind="sixty" size="title" />
                <h2 class="title-subsection text-ink">{{ t("reflections.sixty.tab.title") }}</h2>
                <TabInfoToggle v-if="hasAnyItem" :expanded="infoOpen" :topic="t('reflections.sixty.tab.title')" @toggle="infoOpen = !infoOpen" />
                <a
                    v-if="hasAnyItem && canStart"
                    href="/reflections/360/start"
                    class="base-btn-primary base-btn-sm ml-auto inline-flex items-center gap-2 shrink-0 whitespace-nowrap"
                    @click.prevent="emit('new-360')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    {{ t("reflections.sixty.tab.newButton") }}
                </a>
            </div>
            <p class="body-small text-ink-muted">
                {{ t("reflections.sixty.tab.intro") }}
            </p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <HowItWorks360 v-if="!hasAnyItem" :hide-start="!canStart" @start="emit('new-360')" />

        <BaseDialog v-model:open="infoOpen" :title="t('reflections.sixty.howItWorksTitle')">
            <HowItWorks360 in-dialog />
            <template v-if="canStart" #footer
                ><BaseButton theme="primary" @click="onStartFromInfo">{{ t("reflections.sixty.launch") }}</BaseButton></template
            >
        </BaseDialog>

        <div v-if="activeCycles.length" class="flex flex-col gap-2">
            <div class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.tab.active") }}</div>
            <Active360Card
                v-for="cycle in activeCycles"
                :key="cycle.id"
                :cycle="cycle"
                @invite-more="onInviteMore"
                @remind="onRemind"
                @wrap-up="onWrapUp"
                @open-self="emit('open-self', $event)"
                @extended="emit('extended', $event)" />
        </div>

        <div v-if="pendingTasks.length" class="flex flex-col gap-2 mt-5">
            <div class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.tab.ready") }}</div>
            <SelfTaskRow v-for="task in pendingTasks" :key="task.id" :task="task" :show-icon="false" @deleted="emit('deleted', $event)" @start="onStartTask" />
        </div>

        <div v-if="pastCycles.length" class="flex flex-col gap-2 mt-5">
            <div class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.tab.past") }}</div>
            <PastCycleCard v-for="cycle in pastCycles" :key="cycle.id" :cycle="cycle" kind="sixty" @view="emit('view-cycle', $event)" />
        </div>

        <AssessmentDemoTools :cycle-actions="cycleDemoActions" bootstrap-kind="sixty" @deleted="emit('deleted', $event)" />
    </section>
</template>

<script setup lang="ts">
import type { CycleForSubject, PastCycleSummary, PendingPersonalTask } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import Active360Card from "~vue/pages/reflections/Active360Card.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import HowItWorks360 from "~vue/pages/reflections/HowItWorks360.vue";
import PastCycleCard from "~vue/pages/reflections/PastCycleCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SelfTaskRow from "~vue/pages/reflections/SelfTaskRow.vue";
import TabInfoToggle from "~vue/pages/reflections/TabInfoToggle.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    activeCycles: CycleForSubject[];
    pastCycles: PastCycleSummary[];
    // 360-flavored personal tasks: own 360 cycles in DRAFT (setup not done) and
    // org campaigns the user hasn't started their cycle from yet. Filtering to
    // `!is_self_assessment_only` happens at the caller.
    pendingTasks?: PendingPersonalTask[];
    // False while an active org campaign owns every 360 template.
    canStart?: boolean;
}

const props = withDefaults(defineProps<Props>(), { pendingTasks: () => [], canStart: false });
const emit = defineEmits<{
    (event: "invite-more", cycleId: string): void;
    (event: "remind", inviteId: string): void;
    (event: "wrap-up", cycleId: string): void;
    (event: "view-cycle", cycleId: string): void;
    (event: "nominate", cycleId: string): void;
    (event: "start-from-campaign", payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }): void;
    (event: "open-self", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "new-360"): void;
    (event: "deleted", cycleId: string): void;
    (event: "extended", cycle: CycleForSubject): void;
}>();

function onStartTask(task: PendingPersonalTask) {
    if (task.kind === "cycle_setup") {
        emit("nominate", task.id);
        return;
    }
    if (task.kind === "cycle_setup_campaign") {
        emit("start-from-campaign", { campaignId: task.id, title: task.title, isSelfAssessmentOnly: false });
    }
}

const hasAnyItem = computed(() => props.activeCycles.length > 0 || props.pastCycles.length > 0 || props.pendingTasks.length > 0);
const cycleDemoActions = computed(() => {
    const active = props.activeCycles.map((cycle) => ({
        id: cycle.id,
        label: cycle.template.name,
        canFill: cycle.state === "draft" || cycle.state === "collecting",
    }));
    const setupTasks = props.pendingTasks.filter((task) => task.kind === "cycle_setup").map((task) => ({ id: task.id, label: task.title, canFill: true }));
    const past = props.pastCycles.map((cycle) => ({ id: cycle.id, label: cycle.template.name, canFill: false }));
    // A draft 360 shows up as both an active cycle and a setup task.
    return [...new Map([...active, ...setupTasks, ...past].map((action) => [action.id, action])).values()];
});
const infoOpen = ref(false);

function onStartFromInfo() {
    infoOpen.value = false;
    emit("new-360");
}

function onInviteMore(cycleId: string) {
    emit("invite-more", cycleId);
}
function onRemind(inviteId: string) {
    emit("remind", inviteId);
}
function onWrapUp(cycleId: string) {
    emit("wrap-up", cycleId);
}
</script>
